export enum AttendanceStatus {
  INCOMPLETE = "INCOMPLETE",
  PROVIDER_SUBMITTED = "PROVIDER_SUBMITTED",
  FAMILY_APPROVED = "FAMILY_APPROVED",
  FAMILY_DENIED = "FAMILY_DENIED",
  PAYMENT_RELEASED = "PAYMENT_RELEASED",
  PROVIDER_ON_HOLD = "PROVIDER_ON_HOLD",
}

export interface IAttendance {
  id: number;
  platform: string;
  status: AttendanceStatus;
  child: {
    id: number;
    householdId: number;
    name: string;
  };
  attendedEntireMonth: null | boolean;
  declineReason: null | string;
  periodStartDate: string;
  periodEndDate: string;
  provider: {
    id: number;
    name: string;
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
    };
    phoneNumber: string;
  };
  days: {
    id: number;
    code: (typeof DayStatus)[keyof typeof DayStatus];
    dateAttended: string;
  }[];
  lastModifiedDate: string;
  submittedByProvider: null | string;
  submittedByProviderDate: null | string;
  acceptedByFamily: null | string;
  submittedByFamily: null | string;
  submittedByFamilyDate: null | string;
  termination: null | boolean;
  terminated: null | boolean;
  terminationDate: string | null;
  chargedForMonth: null | boolean;
  amountBilled: null | number;
  submittedViaPaper: null | boolean;
}

export const DayStatus = {
  ATTENDED: "ATTENDED",
  NO_CARE_PROVIDED: "NO_CARE_PROVIDED",
  LAST_DAY: "LAST_DAY",
} as const;

export const DayStatusColor = {
  [DayStatus.ATTENDED]: "bg-slate-800 outline-slate-800",
  [DayStatus.NO_CARE_PROVIDED]:
    "bg-[rgb(133,133,133)] outline-[rgb(8,110,240)]",
} as const;

export const AttendanceStatusTitles = {
  provider: {
    [AttendanceStatus.INCOMPLETE]: "Incomplete",
    [AttendanceStatus.PROVIDER_SUBMITTED]: "Awaiting Family",
    [AttendanceStatus.FAMILY_APPROVED]: "Submitted for payment",
    [AttendanceStatus.FAMILY_DENIED]: "Family rejected",
    [AttendanceStatus.PAYMENT_RELEASED]: "Payment released to Provider",
    [AttendanceStatus.PROVIDER_ON_HOLD]: "Provider app incomplete",
  },
  family: {
    [AttendanceStatus.INCOMPLETE]: "Awaiting provider",
    [AttendanceStatus.PROVIDER_SUBMITTED]: "Awaiting your response",
    [AttendanceStatus.FAMILY_APPROVED]: "Submitted for payment",
    [AttendanceStatus.FAMILY_DENIED]: "Contact provider",
    [AttendanceStatus.PAYMENT_RELEASED]: "Payment released to Provider",
    [AttendanceStatus.PROVIDER_ON_HOLD]: "Provider app incomplete",
  },
};

export const AttendanceStatusColors = {
  provider: {
    [AttendanceStatus.INCOMPLETE]: "warning",
    [AttendanceStatus.PROVIDER_SUBMITTED]: "processing",
    [AttendanceStatus.FAMILY_APPROVED]: "green",
    [AttendanceStatus.FAMILY_DENIED]: "error",
    [AttendanceStatus.PAYMENT_RELEASED]: "green",
    [AttendanceStatus.PROVIDER_ON_HOLD]: "error",
  },
  family: {
    [AttendanceStatus.INCOMPLETE]: "default",
    [AttendanceStatus.PROVIDER_SUBMITTED]: "warning",
    [AttendanceStatus.FAMILY_APPROVED]: "green",
    [AttendanceStatus.FAMILY_DENIED]: "error",
    [AttendanceStatus.PAYMENT_RELEASED]: "green",
    [AttendanceStatus.PROVIDER_ON_HOLD]: "error",
  },
};

/**
 * Determines if the attendance view should be read-only based on status and view type
 * @param status - The current attendance status
 * @param viewType - Whether this is being viewed by 'provider' or 'family'
 * @returns true if the view should be read-only, false if editable
 */
export function isAttendanceReadOnly(
  status: AttendanceStatus,
  viewType: "provider" | "family"
): boolean {
  switch (status) {
    case AttendanceStatus.INCOMPLETE:
      return false; // Neither read-only - both can edit

    case AttendanceStatus.PROVIDER_SUBMITTED:
      return viewType === "provider"; // Provider read-only, family can edit

    case AttendanceStatus.FAMILY_APPROVED:
    case AttendanceStatus.PAYMENT_RELEASED:
    case AttendanceStatus.PROVIDER_ON_HOLD:
      return true; // Both read-only

    case AttendanceStatus.FAMILY_DENIED:
      return viewType === "family"; // Family read-only, provider can edit (resubmit)

    default:
      return true; // Default to read-only for safety
  }
}
